import { EV } from '../core/eventbus.js';
import { Repo } from '../data/schema.js';
import { Inspire } from './inspire.js';

/**
 * 吐纳系统：在蒲团、香炉这类能打坐的器物上坐定，灵气自己往上长。
 *
 * 与一次按 E 的「打坐」区别：
 *  - 打坐是一次买卖（灵气 +6、体力 −6、心情 +4），用完得再按；
 *  - 吐纳是坐功夫，只要人还坐在上面就慢慢长，代价小、见效慢，
 *    体力见底就停，灵气满 999 也停。
 * 饿肚子不停：原先「饿到 0 就坐不住」会把人卡死——没米下锅就没法打坐，
 * 没法打坐就没灵气，成了个死扣。如今饿着也能坐，只是气聚不拢（一半），
 * 还更耗力气，提醒一句「找口吃的再来」。
 *
 * 判据只看「此刻坐在哪件器物上」，且那件器物的交互动作是 'meditate'（蒲团、香炉）。
 * 空地按 C 就地坐、躺床、坐凳歇脚都不算数，那些各有各的玩法。
 */

const TICK = 3;           // 两口吐纳之间隔多少秒（1 现实秒 = 1 游戏分钟，故三刻一息）
const ENERGY_PER = 0.6;   // 一口气耗多少体力
const MOOD_PER = 0.4;     // 一口气回多少心情
const MIN_ENERGY = 5;     // 体力低于此就坐不住了
const MAX_QI = 999;

export const cultivateSystem = {
  name: 'cultivate',
  _acc: 0,        // 攒够 TICK 才吐一口
  _gained: 0,     // 这一坐共长了多少灵气
  _onId: null,    // 正坐在哪件器物上（null = 没在吐纳）
  _warned: false, // 「坐不住了」这句话只说一次，别刷屏
  _hungry: false, // 「饿着坐，气散」同样只说一次

  update(dt, ctx) {
    const { scene, events, db } = ctx;
    if (!db || !scene) return;
    const p = scene.first('transform', 'player');
    if (!p || !p.pose) { this._settle(ctx); return; }

    // 站着不算，坐/躺还得骑在一件真家伙上才算（车上没有姿态这回事）
    const seatId = (!p.veh && p.pose.state !== 'stand' && p.pose.on != null) ? p.pose.on : null;
    const prop = seatId == null ? null : (scene.props || []).find((x) => x.id === seatId);
    const act = prop && prop.def && prop.def.interact ? prop.def.interact.action : null;

    if (act !== 'meditate') { this._settle(ctx); return; }

    // 换了个蒲团接着坐：这一坐重新起算
    if (this._onId !== seatId) {
      this._reset();
      this._onId = seatId;
      events.emit(EV.TOAST, { text: '坐定吐纳，灵气自行生长。', kind: 'info' });
    }

    const me = Repo.getPlayer(db);
    if (me.qi >= MAX_QI) { this._stop(events, '灵气已盈满，再坐也是白搭。', 'info'); return; }
    if (me.energy < MIN_ENERGY) { this._stop(events, '太累了，先歇口气再说。', 'warn'); return; }
    this._warned = false;

    // 饿着坐：不停，只说一句「气聚不拢」
    const hungry = (me.satiety || 0) <= 0;
    if (hungry && !this._hungry) {
      this._hungry = true;
      events.emit(EV.TOAST, { text: '肚子空着，气聚不拢——坐得住，只是慢一半。找口吃的再来。', kind: 'warn' });
    }
    if (!hungry) this._hungry = false;

    this._acc += dt;
    while (this._acc >= TICK) {
      const cur = Repo.getPlayer(db);
      if (cur.qi >= MAX_QI || cur.energy < MIN_ENERGY) { this._acc = 0; break; }
      this._acc -= TICK;
      // 一口气的深浅看境界：境界越高，坐得住，长得多；饿着只剩一半
      const per = Math.max(1, Math.round(Inspire.qiPerBreath(db) * (hungry ? 0.5 : 1)));
      Repo.savePlayer(db, {
        qi: Math.min(MAX_QI, cur.qi + per),
        energy: Math.max(0, cur.energy - ENERGY_PER * (hungry ? 1.6 : 1)),
        mood: Math.min(100, cur.mood + MOOD_PER),
      });
      Inspire.addQi(db, per, events);   // 记进「累计得过多少」，够了就上一层境界
      this._gained += per;
    }
  },

  /** 「坐不住」这类提示只说一次，省得每分钟来一句 */
  _stop(events, text, kind) {
    if (this._warned) return;
    this._warned = true;
    events.emit(EV.TOAST, { text, kind });
  },

  _reset() {
    this._acc = 0;
    this._gained = 0;
    this._warned = false;
    this._hungry = false;
  },

  /** 离开打坐器物（起身、走开、换图）：有收获就报一声 */
  _settle(ctx) {
    if (this._onId == null) return;
    const got = this._gained;
    this._onId = null;
    this._reset();
    if (got > 0) ctx.events.emit(EV.TOAST, { text: `收功起身，这一坐得灵气 ${got} 点。`, kind: 'good' });
  },
};
